<template lang="pug">
.c-library-page
  //- Header Section
  section.c-library-page__header
    .c-library-page__header-content
      h1.c-library-page__heading Library
      p.c-library-page__subtitle Manage media library items and resources

  //- Content Section
  section.c-library-page__content
    //- Filters Section with Actions
    section.c-library-filters-section
      .c-library-filters
        .c-library-filters__search
          el-input(
            v-model="filter"
            :prefix-icon="Search"
            type="text"
            placeholder="Search library items..."
            clearable
            size="large"
          )

        .c-library-filters__actions
          el-button.c-library-action-btn(@click="dialog = true")
            fai(type="fal" icon="plus-circle")
            span Add New

    //- Table Section
    section.c-library-table-section
      el-table(
        :data="filteredItems"
        v-loading="loading"
        @row-click="$router.push(`library/${$event.id}`)"
        style="width: 100%"
      ).clickable-rows
        el-table-column(width="15")

        el-table-column(prop="title" label="Title" min-width="200")
          template(#default="scope")
            .lh-sm
              .font-black {{ scope.row.title }}

        el-table-column(width="80" label="Image")
          template(#default="scope")
            img(:src="scope.row.image").library-image

        el-table-column(prop="details" label="Details" min-width="250")
          template(#default="scope")
            .lh-sm
              .font-medium {{ scope.row.details }}

        el-table-column(width="15")

  dialog-add-library-item(
    v-if="dialog"
    :loading="loading"
    v-model="dialog"
    @save-item="saveItem"
    @close-dialog="dialog = false"
  )
</template>

<script lang="ts">
import {
  reactive, onMounted, defineComponent, ref, computed,
} from 'vue';
import { Search } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';
import DialogAddLibraryItem from './DialogAddLibraryItem.vue';
import shared from '../shared/index';

export default defineComponent({
  name: 'ManageContent_Media_Library',
  components: { DialogAddLibraryItem },
  setup() {
    const {
      findAllMedia,
      doMutateMedia,
      loading,
      dialog,
    } = shared();
    const items = reactive({ items: [] });
    const filter = ref('');

    async function getItems() {
      try {
        loading.value = true;
        items.items = await findAllMedia();
      } catch (err) {
        console.log(err);
      } finally {
        loading.value = false;
      }
    }

    async function saveItem(input) {
      try {
        await doMutateMedia(input);
        ElMessage.success('Library item added');
      } catch (err: any) {
        ElMessage.error(err?.message || 'Could not add the library item');
      }
      await getItems();
    }

    const filteredItems = computed(() => {
      if (filter.value === '') {
        return items.items;
      }
      const regex = new RegExp(`${filter.value}`, 'gi');
      return items.items.filter((x) => {
        if (x.title) {
          return x.title.match(regex);
        }
        if (x.details) {
          return x.details.match(regex);
        }
        return false;
      });
    });

    onMounted(async () => {
      await getItems();
    });

    return {
      items,
      loading,
      dialog,
      filter,
      filteredItems,
      Search,
      // methods
      saveItem,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables - Match Directory page
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-library-page {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 2rem;
    margin-bottom: 0;
  }

  &__header-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__heading {
    font-size: 1.875rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.5;
  }

  &__content {
    margin-top: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}

// Action button styles - Match Directory page
.c-library-action-btn {
  height: 36px;
  font-size: 14px;
  border-radius: 8px;
  background-color: white !important;
  border: 1.5px solid $color-purple-text !important;
  color: $color-purple-text !important;

  &:hover,
  &:focus {
    background-color: rgba($color-purple-text, 0.08) !important;
    border-color: color.adjust($color-purple-text, $lightness: -8%) !important;
    color: color.adjust($color-purple-text, $lightness: -8%) !important;
  }

  :deep(span) {
    font-weight: 500;
  }

  :deep(.me-2) {
    margin-right: 0.5rem;
  }
}

// Filters Section
.c-library-filters-section {
  margin-top: 0;
  margin-bottom: 0;
}

.c-library-filters {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 0;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }

  &__search {
    flex: 1;
    min-width: 0;

    :deep(.el-input) {
      .el-input__wrapper {
        border-radius: 10px;
        border: 1.5px solid $gray-200;
        background: #F0F0F3;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
        padding: 0 12px;
        height: 40px;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

        &:hover {
          border-color: #c0c4cc;
          box-shadow: none;
        }

        &.is-focus {
          border-color: #01beb0;
          box-shadow: none;
        }
      }

      .el-input__inner {
        height: 100%;
        font-size: 0.875rem;
        color: $gray-700;
        background: transparent;

        &::placeholder {
          color: $gray-400;
          font-weight: 400;
        }
      }

      .el-input__prefix {
        color: $gray-400;
        font-size: 1.125rem;

        .el-icon {
          font-size: 1.125rem;
        }
      }

      .el-input__suffix {
        .el-input__clear {
          color: $gray-400;
          transition: color 0.2s ease;

          &:hover {
            color: $color-purple-text;
          }
        }
      }
    }
  }

  &__actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;

    @media (max-width: 767px) {
      width: 100%;
    }
  }
}

// Table Section
.c-library-table-section {
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: none;
  overflow: hidden;

  :deep(.el-table) {
    min-width: 100%;
    border-radius: 0;

    .cell {
      color: $gray-700;
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25rem;
      white-space: nowrap;
      padding: 0.5rem 0.5rem;
    }

    th > .cell {
      text-transform: capitalize;
      color: $gray-700;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      padding: 0.5rem 0.5rem;
    }

    th.el-table__cell.is-leaf {
      background-color: $gray-50;
      border-bottom: 1px solid $gray-100;
      padding: 0.5rem 0;
    }

    td.el-table__cell {
      border-bottom: 1px solid $gray-100;
      padding: 0.625rem 0;
    }

    .el-table__row {
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: $gray-50;
      }
    }

    .el-table__empty-block {
      min-height: 400px;
    }
  }
}

// Font utilities
.font-black {
  font-weight: 600;
  color: $gray-900;
}

.font-medium {
  font-weight: 500;
  color: $gray-700;
}

.lh-sm {
  line-height: 1.25;
}

// Library image styling
.library-image {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
}

// Responsive Design
@media (max-width: 768px) {
  .c-library-page {
    padding: 1.5rem;

    &__header {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }

    &__heading {
      font-size: 1.5rem;
    }

    &__subtitle {
      font-size: 0.8125rem;
    }
  }
}

@media (max-width: 480px) {
  .c-library-page {
    padding: 1rem;

    &__heading {
      font-size: 1.25rem;
    }

    &__subtitle {
      font-size: 0.75rem;
    }
  }
}
</style>
